import { ref } from 'vue';
import { storeToRefs } from 'pinia';

import { Logger } from '@/services/logger';
import {
  getUC,
  isUCConsentsChangeEvent,
  isNotOnInitialPageLoadUCConsentsChangeEvent,
  isUCInitializedEvent,
} from '@/utils/tracking/usercentricsV3';
import { isCurrentDomainAllowed } from '@/utils/settings';
import { isDev, isProd, isStaging, isTestCookiePresent } from '@/utils/environment';

import { useRootStore } from '@/stores/root';
import { useUserStore } from '@/stores/user';
import { useTrackingStore } from '@/stores/tracking';
import { UcV3Event } from '@/types/usercentrics/v3/enums';

export const useConsentManager = () => {
  const { getCMPData } = useRootStore();

  const userStore = useUserStore();
  const { getIsUserLoggedIn } = userStore;
  const { isUserLoggedIn } = storeToRefs(userStore);

  const trackingStore = useTrackingStore();
  const { isTrackingInitialized, initialControllerId, userTrackingConsents } =
    storeToRefs(trackingStore);
  const {
    initializeTracking,
    sendTrackingData,
    setTrackingSettings,
    checkDoesTrackingDataExistLocally,
    saveInitializationControllerIdLocally,
    saveChangeEventUserInteractionLocally,
    saveChangeEventTimestampLocally,
    isLocalTrackingDataNewest,
    clearLocalTrackingData,
    getConsentDetails,
    showConsentsBannerFirstLayer,
    suppressUsercentricsCMPDisplay,
    getConsentTimestamp,
    saveConsentServicesToLocalStorage,
  } = trackingStore;

  const shouldSendTrackingDataOnInit = ref(false);
  const externalEventsThatShouldOpenFirstLayer = ['consentbanner.open.settings'];

  function shouldInit() {
    const isDomainAllowed = isCurrentDomainAllowed();
    return isDomainAllowed || isDev();
  }

  async function initConsentManager() {
    handleSuppressTrackingConsentBanner();

    const isUserLoggedIn = await getIsUserLoggedIn();

    setTrackingSettings();
    registerConsentManagerListeners();

    if (isUserLoggedIn) {
      await getCMPData();
      await handleLoggedInUserTrackingInitialization();
    } else {
      initializeTrackingWithoutExistingControllerId();
    }
  }

  function handleSuppressTrackingConsentBanner() {
    if ((isStaging() || isProd()) && isTestCookiePresent()) {
      suppressUsercentricsCMPDisplay();
    }
  }

  function initializeTrackingWithoutExistingControllerId() {
    Logger.logInfo('Initializing tracking without existing controllerId');

    initializeTracking();
  }

  async function handleLoggedInUserTrackingInitialization() {
    const existingUserTrackingConsents = userTrackingConsents.value;

    if (existingUserTrackingConsents) {
      handleKnownUserTrackingInitialization(existingUserTrackingConsents.controllerId);
    } else {
      if (checkDoesTrackingDataExistLocally()) {
        shouldSendTrackingDataOnInit.value = true;
      }

      initializeTrackingWithoutExistingControllerId();
    }
  }

  /**
   * Known user here means a user who is already connected to their tracking consents.
   */
  async function handleKnownUserTrackingInitialization(userControllerId?: string | null) {
    if (checkDoesTrackingDataExistLocally() && isLocalTrackingDataNewest()) {
      shouldSendTrackingDataOnInit.value = true;
      initializeTrackingWithoutExistingControllerId();
    } else {
      Logger.logInfo("Initialize tracking with user's controllerId");
      await clearLocalTrackingData();
      initializeTracking(userControllerId);
    }
  }

  function registerConsentManagerListeners() {
    registerInternalEventListeners();
    registerExternalEventListeners();
  }

  function unregisterConsentManagerListeners() {
    unregisterInternalEventListeners();
    unregisterExternalEventListeners();
  }

  /**
   * Events that are coming from Usercentrics
   */
  function registerInternalEventListeners() {
    window.addEventListener(UcV3Event.Initialized, handleTrackingInitialized);
    window.addEventListener(UcV3Event.Consent, handleTrackingConsentsChange);
    window.addEventListener(UcV3Event.ViewChanged, handleModifyCMPStyles);
  }

  function unregisterInternalEventListeners() {
    window.removeEventListener(UcV3Event.Initialized, handleTrackingInitialized);
    window.removeEventListener(UcV3Event.Consent, handleTrackingConsentsChange);
    window.removeEventListener(UcV3Event.ViewChanged, handleModifyCMPStyles);
  }

  /**
   * Events that are coming from outside the CMP
   */
  function registerExternalEventListeners() {
    externalEventsThatShouldOpenFirstLayer.forEach(eventName => {
      document.addEventListener(eventName, showConsentsBannerFirstLayer);
    });
  }

  function unregisterExternalEventListeners() {
    externalEventsThatShouldOpenFirstLayer.forEach(eventName => {
      document.removeEventListener(eventName, showConsentsBannerFirstLayer);
    });
  }

  /**
   * This function will modify the default CMP Browser UI styles.
   * Styling is inconsistent between Usercentrics V2 and V3 and we can fix them here.
   */
  function handleModifyCMPStyles() {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(`
      .purposes-list,
      .features-list,
      .list-title {
        display: none;
        }
       footer {
        margin-top: 10px;
      }
    `);

    const customElem = document.querySelector('#usercentrics-cmp-ui');

    if (customElem) {
      const shadowRoot = customElem.shadowRoot;

      if (shadowRoot) {
        shadowRoot.adoptedStyleSheets.push(sheet);
      }
    }
  }

  async function handleTrackingInitialized(event: Event) {
    if (isUCInitializedEvent(event)) {
      isTrackingInitialized.value = true;

      const controllerId = await getUC()?.getControllerId();
      initialControllerId.value = controllerId;
      saveInitializationControllerIdLocally(controllerId);

      if (shouldSendTrackingDataOnInit.value) {
        await handleSendLocalTrackingData();
      }
    }
  }

  async function handleSendLocalTrackingData() {
    const localTrackingData = await getConsentDetails();

    if (localTrackingData) {
      await sendTrackingData(localTrackingData);
      shouldSendTrackingDataOnInit.value = false;
    }
  }

  async function handleTrackingConsentsChange(event: Event) {
    if (isUCConsentsChangeEvent(event)) {
      if (isNotOnInitialPageLoadUCConsentsChangeEvent(event)) {
        saveConsentServicesToLocalStorage(event.detail);
      }

      if (isTrackingInitialized.value) {
        saveChangeEventUserInteractionLocally();
        const consentTimestamp = getConsentTimestamp(event.detail);
        saveChangeEventTimestampLocally(consentTimestamp);
        if (isUserLoggedIn.value) {
          sendTrackingData(event.detail);
        }
      }
    }
  }

  function destroyConsentManager() {
    unregisterConsentManagerListeners();
  }

  return {
    shouldInit,
    initConsentManager,
    destroyConsentManager,
    registerConsentManagerListeners,
    externalEventsThatShouldOpenFirstLayer,
    handleTrackingConsentsChange,
  };
};
